import React from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { Editor } from '@/components/ui/editor'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { CourseCertificateFields } from '@/components/features/courses/CourseCertificateFields'
import { emptyCertificateDraft } from '@/components/features/courses/certificateText'
import { useAuth } from '@/lib/AuthContext'
import type { CoursesModuleConfig } from '@/types/space-wizard'

type Draft = NonNullable<CoursesModuleConfig['course']>

export const emptyCourseDraft = (): Draft => ({ title: '', level: 'beginner', description: '' })

const LEVELS = ['beginner', 'intermediate', 'advanced'] as const

/** The "Next" step of a one-course space: the course that is born with it, always as a draft. */
export const CoursesSingleStep: React.FC<{
  draft: Draft
  onChange: (draft: Draft) => void
  titleError?: string
  levelError?: string
  certificateErrors?: { templateId?: string; text?: string }
  disabled?: boolean
}> = ({ draft, onChange, titleError, levelError, certificateErrors, disabled = false }) => {
  const { t } = useTranslation(['common', 'courses'])
  const { hasRole } = useAuth()
  return (
    <div className="min-w-0 space-y-4">
      <div className="space-y-1">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-900">{t('common:spaces.stepCourse')}</h3>
        <p className="text-xs text-gray-500">{t('common:modules.settings.courses.stepHint')}</p>
      </div>
      <div className="grid gap-2">
        <label htmlFor="single-course-title" className="text-sm font-medium text-gray-700">
          {t('courses:form.title')} <span className="text-red-500">*</span>
        </label>
        <Input id="single-course-title" value={draft.title} disabled={disabled} maxLength={200} aria-invalid={Boolean(titleError)}
          onChange={(e) => onChange({ ...draft, title: e.target.value })} placeholder={t('courses:form.titlePlaceholder')} />
        {titleError && <p className="text-xs text-red-600">{titleError}</p>}
      </div>
      <div className="grid gap-2">
        <label className="text-sm font-medium text-gray-700">{t('courses:form.level')}</label>
        <Select value={draft.level} disabled={disabled} onValueChange={(value) => onChange({ ...draft, level: value as Draft['level'] })}>
          <SelectTrigger aria-invalid={Boolean(levelError)}><SelectValue /></SelectTrigger>
          <SelectContent>
            {LEVELS.map((value) => <SelectItem key={value} value={value}>{t(`courses:${value}`)}</SelectItem>)}
          </SelectContent>
        </Select>
        {levelError && <p className="text-xs text-red-600">{levelError}</p>}
      </div>
      <div className="grid gap-2">
        <label className="text-sm font-medium text-gray-700">{t('courses:form.description')}</label>
        <Editor aiFieldKind="course" content={draft.description} onChange={(description) => onChange({ ...draft, description })} className="min-h-[132px] sm:min-h-[200px]" />
      </div>
      {/* The course is born without lessons, so the workload is only explained here. */}
      {hasRole(['owner', 'admin']) && (
        <CourseCertificateFields
          draft={draft.certificate ?? emptyCertificateDraft()}
          onChange={(certificate) => onChange({ ...draft, certificate })}
          courseTitle={draft.title}
          defaultText={t('courses:certificateForm.defaultText')}
          workloadHours={undefined}
          lessonsWithoutDuration={null}
          errors={certificateErrors}
          disabled={disabled}
        />
      )}
      <p className="text-xs text-gray-500">{t('courses:form.bornDraft')}</p>
    </div>
  )
}
